import type { Side, Trade, TradeFill, WalletTier } from "../api/types";
import * as money from "./money";

/**
 * Разбор истории наших сделок (веха M6).
 *
 * Чистый модуль без React по тому же правилу, что lib/tier.ts: логика живёт в
 * lib/*.ts, потому что vitest в этом проекте включает только `*.test.ts`.
 *
 * Главный вопрос, на который отвечает модуль: что в строке сделки НАШЕ, а что
 * чужое. Событие приватного канала описывает тейкер-ордер целиком, поэтому
 * price и size сделки — это цена и размер агрессора, и в сделке, где мейкером
 * были мы, они принадлежат другому исходу (живой пример: наши 8.21 share по
 * 0.73 приехали строкой «173.387665 @ 0.29»).
 */

/** Мы были мейкером в этой сделке: агрессор пришёл на наш стоящий ордер. */
export function isMaker(trade: Trade): boolean {
  if (trade.trader_side) return trade.trader_side === "MAKER";
  // Запись из до-M6 журнала: роли в ней нет, но наша собственная встречная
  // строка есть только у мейкерской сделки.
  return (trade.fills ?? []).some((f) => f.ours);
}

/**
 * Наш филл: размер и цена.
 *
 * Считается трейдером и приезжает готовым; здесь только выбор источника, потому
 * что у старых записей our_size пуст, а показать что-то честное всё равно надо.
 * Для тейкерской сделки сделка и есть наш филл, для мейкерской — сумма наших
 * встречных строк.
 */
export function ourFill(trade: Trade): { size: string; price: string } | null {
  if (trade.our_size && trade.our_price) {
    return { size: trade.our_size, price: trade.our_price };
  }
  if (!isMaker(trade)) return { size: trade.size, price: trade.price };

  const ours = (trade.fills ?? []).filter((f) => f.ours);
  if (ours.length === 0) return null;

  let size = 0;
  let notional = 0;
  for (const f of ours) {
    const s = money.parse(f.size);
    const p = money.parse(f.price);
    if (s === null || p === null) return null;
    const line = money.mul(s, p);
    const sum = money.add(size, s);
    if (line === null || sum === null) return null;
    size = sum;
    const acc = money.add(notional, line);
    if (acc === null) return null;
    notional = acc;
  }
  if (size === 0) return null;
  const price = money.div(notional, size);
  if (price === null) return null;
  return { size: money.format(size), price: money.format(price) };
}

/**
 * НАША сторона сделки: покупали мы или продавали.
 *
 * Берётся из нашей собственной встречной строки, а НЕ выводится обращением
 * стороны сделки — и это разные ответы. Матч на Polymarket бывает «чеканкой»:
 * тейкер покупает Down по 0.16, а мейкеры покупают Up по 0.84 — обе стороны
 * ПОКУПАЮТ дополняющие исходы. Обращение выдавало такую сделку за продажу,
 * и в истории напротив ордера, поставленного кнопкой «Купить UP», стояло
 * «продажа 0.84»: цена от нашего токена, направление — от чужого.
 *
 * Обращение осталось единственным способом ответить для записей до вехи M6: у
 * них встречных строк нет вовсе, а в обычном матче (тейкер покупает, мейкер
 * продаёт тот же токен) оно верно.
 */
export function ourSide(trade: Trade): Side {
  if (!isMaker(trade)) return trade.side;
  const ours = (trade.fills ?? []).find((f) => f.ours);
  if (ours?.side) return ours.side;
  return trade.side === "BUY" ? "SELL" : "BUY";
}

/**
 * Исход, который достался НАМ: «Up», «Down» и так далее.
 *
 * Без него сторона сделки не читается: «покупка 0.84» в мейкерской сделке
 * относится к нашему токену, а цена и размер самой сделки — к чужому.
 */
export function ourOutcome(trade: Trade): string | undefined {
  const ours = (trade.fills ?? []).find((f) => f.ours);
  return ours?.outcome || undefined;
}

/** Наш нотионал сделки в долларах. */
export function ourNotional(trade: Trade): string | null {
  const fill = ourFill(trade);
  if (!fill) return null;
  const size = money.parse(fill.size);
  const price = money.parse(fill.price);
  if (size === null || price === null) return null;
  const value = money.mul(size, price);
  return value === null ? null : money.format(value);
}

/**
 * Контрагенты сделки — те, кто СТОЯЛ ПРОТИВ нас.
 *
 * Пустой список у мейкерской сделки не бедность данных, а факт: на проводе
 * видны только мейкеры, и когда мейкером были мы, адреса агрессора нет вовсе.
 * Оставшиеся строки — соседи по сносу, и выдавать их за контрагентов значило бы
 * показывать оператору профиль того, с кем он не торговал.
 */
export function counterpartyFills(trade: Trade): TradeFill[] {
  if (isMaker(trade)) return [];
  return (trade.fills ?? []).filter((f) => !f.ours);
}

/**
 * Агрессор мейкерской сделки — тот, кто снял НАШУ заявку (ADR-0008).
 *
 * Единственный настоящий контрагент такой сделки. На проводе его нет (видны
 * только мейкеры), поэтому адрес приезжает из квитанции транзакции и ПОЗЖЕ
 * самой сделки — отдельной публикацией той же записи.
 *
 * У тейкерской сделки не бывает по определению: тейкером были мы сами, и
 * показать здесь свой же адрес значило бы объявить оператора своим контрагентом.
 */
export function takerAddress(trade: Trade): string | undefined {
  if (!isMaker(trade)) return undefined;
  return trade.taker_address || undefined;
}

/** Мейкеры, снесённые тем же тейкером, что и мы. Не контрагенты. */
export function neighbourFills(trade: Trade): TradeFill[] {
  if (!isMaker(trade)) return [];
  return (trade.fills ?? []).filter((f) => !f.ours);
}

/**
 * Встреченные кошельки записи, сделанной ДО вехи: адреса есть, сумм и роли нет.
 *
 * Такие строки лежат в журнале с M4, и молча показывать в них прочерк значило
 * бы потерять единственное, что о контрагентах вообще было записано. Но и
 * называть их контрагентами нельзя: роль в сделке не сохранялась, а без неё
 * тот же самый массив означает разное (ADR-0007 §1). Отсюда отдельная функция и
 * отдельный вид на экране.
 */
export function legacyAddresses(trade: Trade): string[] {
  if ((trade.fills ?? []).length > 0) return [];
  return trade.counterparties ?? [];
}

/**
 * Самая крупная встречная строка.
 *
 * В таблицу помещается один контрагент, и показывать надо того, кто налил
 * больше всех, а не первого в массиве: порядок задаёт биржа, и он не значит
 * ничего. Тир для выбора не годится — «крупный на 0.5 share» вводит в
 * заблуждение сильнее, чем «неизвестный на 40».
 */
export function largestFill(fills: readonly TradeFill[]): TradeFill | undefined {
  let best: TradeFill | undefined;
  let bestSize = -1;
  for (const f of fills) {
    const size = money.parse(f.size) ?? 0;
    if (size > bestSize) {
      best = f;
      bestSize = size;
    }
  }
  return best;
}

/**
 * Тир контрагента для показа: снимок на момент сделки, а при его отсутствии —
 * то, что известно сейчас.
 *
 * Приоритет у снимка, и это не мелочь: горизонты профиля скользящие, и
 * сегодняшний sharp через неделю станет noise. Разбор «против кого я стоял»
 * обязан идти по тому, кем контрагент был тогда.
 */
export function fillTier(
  fill: TradeFill,
  current: Readonly<Record<string, WalletTier>>,
): { tier: WalletTier | undefined; snapshot: boolean } {
  if (fill.tier) return { tier: fill.tier, snapshot: true };
  return { tier: current[fill.address], snapshot: false };
}

/**
 * Наша комиссия сделки в долларах.
 *
 * null означает «не посчитана» — расписание рынка не было известно, — и это НЕ
 * ноль: ноль здесь законное значение и означает «заплатил контрагент», потому что
 * комиссию платит только тейкер. Показывать одно вместо другого нельзя: первое —
 * незнание, второе — знание.
 */
export function ourFee(trade: Trade): string | null {
  return trade.our_fee ?? null;
}

/** Итог по странице истории: сколько сделок, на сколько и в какой роли. */
export interface TradesSummary {
  count: number;
  makerCount: number;
  notional: string;
  /** Заплаченная нами комиссия за все сделки страницы. */
  fees: string;
  /**
   * Оборот С УЧЁТОМ комиссии — то, во что сделки обошлись на самом деле.
   *
   * Комиссия входит сюда СО ЗНАКОМ, а не вычитается: у покупки она платится СВЕРХ
   * нотионала («$5» тейкерской покупки стоили $5.28 — измерено на цепочке), у
   * продажи удерживается из выручки. Вычитание в обе стороны занижало бы покупки
   * на два размера комиссии и читалось бы как «сделки обошлись дешевле номинала»
   * ровно там, где они обошлись дороже.
   */
  notionalNet: string;
  /**
   * У скольких сделок комиссия неизвестна. Ненулевое значение обязано быть видно:
   * иначе итог по комиссии выглядел бы полным, будучи посчитанным по половине.
   */
  feesUnknown: number;
  wallets: number;
}

export function summarize(trades: readonly Trade[]): TradesSummary {
  let notional = 0;
  let fees = 0;
  // Поправка к обороту: у покупки комиссия прибавляется, у продажи вычитается.
  let adjust = 0;
  let makerCount = 0;
  let feesUnknown = 0;
  const wallets = new Set<string>();

  for (const t of trades) {
    if (isMaker(t)) makerCount++;
    const value = ourNotional(t);
    const parsed = value === null ? null : money.parse(value);
    if (parsed !== null) {
      const sum = money.add(notional, parsed);
      if (sum !== null) notional = sum;
    }
    const fee = ourFee(t);
    const feeParsed = fee === null ? null : money.parse(fee);
    if (feeParsed === null) {
      feesUnknown++;
    } else {
      const sum = money.add(fees, feeParsed);
      if (sum !== null) fees = sum;
      // Сторона берётся из сделки, и это не приближение: ненулевая комиссия
      // бывает только там, где тейкером были МЫ, а у тейкерской сделки её
      // сторона и есть наша.
      const signed = t.side === "BUY" ? feeParsed : -feeParsed;
      const next = money.add(adjust, signed);
      if (next !== null) adjust = next;
    }
    for (const f of counterpartyFills(t)) wallets.add(f.address);
  }

  // Не «оборот со скидкой», а деньги, которые действительно двинулись: покупка
  // унесла из кошелька нотионал ВМЕСТЕ с комиссией, продажа принесла выручку за
  // её вычетом.
  const net = money.add(notional, adjust);
  return {
    count: trades.length,
    makerCount,
    notional: money.format(notional),
    fees: money.format(fees),
    notionalNet: money.format(net ?? notional),
    feesUnknown,
    wallets: wallets.size,
  };
}

/**
 * Два состояния ОДНОЙ сделки: позднее побеждает, но не отменяет узнанного.
 *
 * Замещение по id само по себе верно — сделка шагает `MATCHED → MINED →
 * CONFIRMED`, и запись обязана обновляться, а не дублироваться. Неверно другое:
 * считать, что каждый шаг знает о сделке всё.
 *
 * `taker_address` не знает ни один из них. Адреса агрессора на проводе нет по
 * построению (видны только мейкеры, ADR-0007 §1); он приезжает из квитанции
 * транзакции ОТДЕЛЬНОЙ публикацией той же сделки, между шагами статуса. Разбор
 * на проде 13.08.2026: `MATCHED` без адреса, `MINED` без, `MATCHED` с
 * восстановленным адресом, `CONFIRMED` — снова без, и он стирал восстановленное
 * ровно там, где оператор на него смотрит.
 *
 * Правило узкое намеренно: сохраняются ТОЛЬКО этот адрес и аккаунт сделки. Оба
 * узнаются один раз и не разузнаются, тогда как остальные поля шаг статуса
 * приносит целиком, и «склеивать» их значило бы удерживать на экране устаревшее.
 * Снимок тира внутри встречных строк лечится своим правилом — см. `fillTier`.
 */
export function mergeTrade(prev: Trade | undefined, next: Trade): Trade {
  if (!prev) return next;
  // Аккаунт — по тому же правилу, что адрес агрессора: журнал его знает, а
  // живой шаг статуса до 2026-10-07 приезжал без него, и позиция аккаунта
  // (lib/holdings.scopeFills) теряла сделку при первом же шаге.
  const taker = next.taker_address || prev.taker_address;
  const account = next.account || prev.account;
  if (taker === next.taker_address && account === next.account) return next;
  return {
    ...next,
    ...(taker ? { taker_address: taker } : {}),
    ...(account ? { account } : {}),
  };
}

/**
 * Ключ записи сделки: id биржи и аккаунт.
 *
 * Двое своих бывают мейкерами одной сделки — когорта ставит каждому
 * одинаковую заявку по одной цене, и агрессор исполняет их одним `id`. Доля у
 * каждого своя (журнал хранит строку на аккаунт, миграция 00064), и запись по
 * одному `id` оставила бы на экране только последнюю пришедшую. Без аккаунта —
 * публичный принт или строка старой сборки — ключом остаётся `id`.
 */
export function tradeKey(t: Pick<Trade, "id" | "account">): string {
  return t.account ? `${t.id}|${t.account}` : t.id;
}

/**
 * Журнал плюс живые сделки — по одной записи на сделку аккаунта (`tradeKey`).
 *
 * Нужно ровно там, где журнал спрашивают ОПРОСОМ, а ответ нужен сразу.
 * Контрагент в панели ордеров брался только из `GET /trades`, который ходит раз
 * в полминуты, тогда как та же сделка приезжает в сокет через миллисекунды
 * после исполнения (её кладёт в стор филлов dispatch). Полминуты прочерка
 * напротив собственного `MATCHED` читаются как «контрагента не было» — а
 * разбирают исполнение именно в эти секунды.
 *
 * При совпадении id побеждает ЖИВАЯ запись: одна сделка приезжает несколько
 * раз, шагая `MATCHED → MINED → CONFIRMED`, и страница журнала, прочитанная до
 * последнего шага, откатывала бы статус назад.
 *
 * Побеждает, но не затирает адрес агрессора (см. mergeTrade): его знает как раз
 * журнал — в базе он защищён от затирания, — а живая запись приезжает без него.
 * Без этой оговорки опрос `GET /trades`, который адрес возвращает, не чинил бы
 * ничего: живая запись перекрывала бы его каждые полминуты заново.
 *
 * Порядок — журнальный, живые новинки в хвосте: потребители фильтруют список по
 * ордеру, а не показывают его как есть.
 */
export function mergeTrades(journal: readonly Trade[], live: readonly Trade[]): Trade[] {
  if (live.length === 0) return [...journal];
  const byKey = new Map<string, Trade>();
  for (const t of journal) byKey.set(tradeKey(t), t);
  for (const t of live) byKey.set(tradeKey(t), mergeTrade(byKey.get(tradeKey(t)), t));
  return [...byKey.values()];
}

/**
 * Сделки, закрывшие НАШ ордер.
 *
 * Связь ордера со сделкой не хранится ни одним полем: на проводе её нет, и
 * восстанавливается она с двух сторон в зависимости от нашей роли.
 *
 *   - были ТЕЙКЕРОМ — наш ордер и есть агрессор сделки, `taker_order_id`;
 *   - были МЕЙКЕРОМ — наш ордер лежит среди встречных строк с `ours`.
 *
 * Ровно поэтому функция не сводится к сравнению одного поля. Взять только
 * первую половину значило бы потерять контрагентов у всех мейкерских
 * исполнений — то есть у тех, ради которых стакан и держат.
 *
 * Сопоставляется `order_id` БИРЖИ, а не client_order_id: во встречных строках
 * приходит только он. У ордера, не дошедшего до биржи, идентификатора нет, и
 * сопоставлять нечего — такой ордер честно остаётся без контрагентов.
 */
export function tradesForOrder(
  orderID: string | null | undefined,
  trades: readonly Trade[],
): Trade[] {
  if (!orderID) return [];
  return trades.filter(
    (t) =>
      t.taker_order_id === orderID ||
      (t.fills ?? []).some((f) => f.ours && f.order_id === orderID),
  );
}

/**
 * Что показать в колонке «контрагент» у ордера — и почему именно это.
 *
 * Три исхода, и они означают РАЗНОЕ:
 *
 *   - `counterparties` — мы были тейкером, сняли чужие ордера, вот они;
 *   - `maker` — мы стояли в стакане, и нас снял агрессор. Его адреса на проводе
 *     нет вовсе (ADR-0007 §1), и ждать нечего: он не появится ни через минуту,
 *     ни через час;
 *   - `unknown` — сделки по ордеру мы ещё не видели.
 *
 * Различие не косметическое, и ради него функция и заведена. Раньше два
 * последних случая выглядели одинаковым прочерком с подсказкой «либо не
 * доехало, либо мейкером были мы»: оператор ждал данных, которых не будет.
 */
export type OrderFillers =
  | { kind: "counterparties"; fills: TradeFill[] }
  | { kind: "taker"; address: string }
  | { kind: "maker"; takerOrderID: string | undefined }
  | { kind: "unknown" };

export function orderFillers(
  orderID: string | null | undefined,
  trades: readonly Trade[],
): OrderFillers {
  const fills = counterpartiesForOrder(orderID, trades);
  if (fills.length > 0) return { kind: "counterparties", fills };

  const matched = tradesForOrder(orderID, trades);
  // Ордер исполнялся ЧАСТЯМИ и обеими ролями — редко, но бывает: GTC пересёк
  // стакан краем и остатком встал в него. Раз хоть одна сделка тейкерская, а
  // контрагентов в ней нет, ждать встречные строки всё ещё осмысленно.
  if (matched.length === 0 || !matched.every(isMaker)) return { kind: "unknown" };

  // Агрессор, восстановленный из цепочки, — настоящий контрагент мейкерского
  // исполнения (ADR-0008). Приезжает секундами позже сделки, поэтому «его ещё
  // нет» остаётся отдельным состоянием ниже, а не сводится к нему.
  const taker = matched.map(takerAddress).find((a) => a);
  if (taker) return { kind: "taker", address: taker };

  return { kind: "maker", takerOrderID: matched.find((t) => t.taker_order_id)?.taker_order_id };
}

/**
 * Кто налил по нашему ордеру: встречные строки всех его сделок.
 *
 * Дубли по адресу схлопываются, а размеры складываются: один маркет-мейкер
 * закрывает крупный ордер несколькими строками, и три чипа с одним и тем же
 * кошельком читаются как три разных контрагента.
 */
export function counterpartiesForOrder(
  orderID: string | null | undefined,
  trades: readonly Trade[],
): TradeFill[] {
  const byAddress = new Map<string, TradeFill>();
  for (const trade of tradesForOrder(orderID, trades)) {
    for (const fill of counterpartyFills(trade)) {
      const seen = byAddress.get(fill.address);
      if (!seen) {
        byAddress.set(fill.address, { ...fill });
        continue;
      }
      const sum = money.add(money.parseOr(seen.size, 0), money.parseOr(fill.size, 0));
      if (sum !== null) seen.size = money.format(sum);
      // Тир берётся самый поздний из известных: пустой означает «тогда не
      // знали», и затирать им уже известный — терять квалификацию.
      if (fill.tier) seen.tier = fill.tier;
    }
  }
  // По убыванию размера: крупнейший контрагент решает, что это была за сделка.
  return [...byAddress.values()].sort(
    (a, b) => money.parseOr(b.size, 0) - money.parseOr(a.size, 0),
  );
}
